import { Observable, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { map, take, tap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';

import { SeatMapFacade } from '@seat-map/seat-map.facade';
import {
  PassengersSeatMap,
  PassengersSeats, SeatMap, SeatMapErrors, SeatMapErrorsTypes, SeatMapTransactions, StateSeatTypes
} from '@seat-map/entities/seat-map';
import { AirValidationFlightsSector, AirValidationSector, ItineraryDetail } from '@seat-map/entities/itinerary-detail';
import { AppFacade } from '@app/app.facade';
import { loaderColors } from '@ux/entities/loader-colors';
import { ReservationType } from '@user-history/entities/user-history-item';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { AvoService } from '@avo/services/avo.service';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';

export abstract class SeatMapAbstract {

  protected AvoDispatch = new AvoDispatchOrchestrator();

  public itineraryDetail: ItineraryDetail;
  public showSelectSector: boolean;
  public showDetailSeats: boolean;
  public heightDetailSeats: number;
  public topDetailSeats: number;
  public heightItemSeat: number;
  public marginItemSeat: number;
  public heightItemSeatDefault: number;
  public supportImg: string;
  public seatMapTransactions: SeatMapTransactions[] = [];
  public subscriptions: Subscription[] = [];
  public loaderColor: string = loaderColors.WHITE;
  public showError: boolean;
  public seatMapErrors: SeatMapErrors;
  public isLoadingSeatMapFlight;

  constructor(
    protected facade: SeatMapFacade,
    protected appFacade: AppFacade,
    protected router: Router,
    protected domManiputaleService: DomManipulateService,
    protected translateService: TranslocoService,
    protected mobileDeviceService: MobileDeviceService,
    protected avoService?: AvoService,
  ) {
    this.showSelectSector = false;
    this.showDetailSeats = false;
    this.heightItemSeat = 50;
    this.marginItemSeat = 30;
    this.heightItemSeatDefault = 66;
    this.supportImg = 'ilustra-soporte.jpg';
    this.showError = false;
    this.isLoadingSeatMapFlight = true;
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get showError$(): Observable<boolean> {
    return this.facade.showError$;
  }

  get seatMapError$(): Observable<SeatMapErrors> {
    return this.facade.seatMapError$;
  }

  get userId$(): Observable<string | number> {
    return this.appFacade.user$.pipe(map(user => user ? user.id : null));
  }

  get seatMap$(): Observable<SeatMap> {
    return this.facade.seatMap$;
  }

  get isLoadingSeatMapTransactions(): boolean {
    return this.seatMapTransactions?.filter(x => x.isLoading)?.length > 0;
  }

  get isLoadingSeatMapFlight$(): Observable<boolean> {
    return this.facade.isLoadingSeatMapFlight$;
  }

  get seatMapTransactions$(): Observable<SeatMapTransactions[]> {
    return this.facade.seatMapTransactions$;
  }

  get itineraryDetail$(): Observable<ItineraryDetail> {
    return this.facade.itineraryDetail$;
  }

  get isLoadingSeatMap$(): Observable<boolean> {
    return this.facade.isLoadingSeatMap$;
  }

  get seatMapSeat$(): Observable<PassengersSeats[]> {
    return this.facade.seatMapSeats$;
  }

  get sectorAndFlightSelected$(): Observable<AirValidationSector> {
    return this.facade.sectorAndFlightSelected$;
  }

  public leakedSeatMap(seats: PassengersSeats[] = []): PassengersSeats[] {
    return seats ? seats.filter(seat => {
      if (seat.State === StateSeatTypes.Removed) {
        return seat.SeatNumber !== seat.SeatNumberRemoved;
      } else {
        return seat;
      }
    }) : [];
  }

  public saveSeatsConfirm(seatMap: SeatMap, itinerary: ItineraryDetail): void {
    if (!this.isLoadingSeatMapTransactions) {
      const seats = this.leakedSeatMap(seatMap?.Seats);
      if (seats?.filter(x => x.State === StateSeatTypes.Origin)?.length ===
        seatMap?.Seats.length) {
        this.isSelectNextFlight();
      } else {
        const flightIdx = this.getFlightIdxByFlight(itinerary);
        this.facade.fetchSaveSeatsPassengersInFlightAction(seatMap, itinerary,
          flightIdx, this.isNextFlight(itinerary, flightIdx));
      }
    }
  }

  public getFlightIdxByFlight(itinerary: ItineraryDetail): number {
    const allFlights: AirValidationFlightsSector[] = [];
    itinerary?.Sectors.forEach(sector => {
      sector?.Flights.forEach(flight => {
        allFlights.push(flight);
      });
    });
    const findIndex = allFlights.findIndex(x => x.IsSelected);
    return findIndex < 0 ? 0 : findIndex;
  }

  private isNextFlight(itinerary: ItineraryDetail, flightIdx: number): boolean {
    const allFlights = [];
    itinerary?.Sectors?.forEach(sector => {
      sector?.Flights?.forEach(flight => {
        allFlights.push(flight);
      });
    });
    return allFlights.length - 1 > flightIdx;
  }

  public closeError(seatMapError: SeatMapErrors): void {
    this.facade.closeErrorSeatMapAction();
    switch (seatMapError.ErrorType) {
      case SeatMapErrorsTypes.SeatsUpdateSucces:
        this.navigateToItinerary();
        break;
      case SeatMapErrorsTypes.GetItinerary:
      case SeatMapErrorsTypes.ItineraryNotAvailable:
      case SeatMapErrorsTypes.GetSeatsSeatMapFlightCheckInAirline:
        this.appFacade.navigateToLastRoute();
        break;
      case SeatMapErrorsTypes.GetSeatsSeatMapFlight:
      case SeatMapErrorsTypes.GetSeatMapFlight:
      case SeatMapErrorsTypes.SeatsNotAvailableInFlight:
        if (!this.isSelectNextFlight()) {
          this.appFacade.navigateToLastRoute();
        }

        break;
    }
  }

  public otherOption(seatMapError: SeatMapErrors): void {
    this.facade.closeErrorSeatMapAction();
    switch (seatMapError.ErrorType) {
      case SeatMapErrorsTypes.GetSeatsSeatMapFlight:
      case SeatMapErrorsTypes.GetSeatMapFlight:
      case SeatMapErrorsTypes.SeatsNotAvailableInFlight:
        this.appFacade.navigateToLastRoute();
        break;
    }
  }

  public isSelectNextFlight(): boolean {
    const currentSectorIndex = this.itineraryDetail?.Sectors?.findIndex(x => x.IsSelected);
    const currentFlightIndex = this.itineraryDetail?.Sectors[currentSectorIndex]?.Flights?.findIndex(x => x.IsSelected);
    let totalFlights = 0;
    this.itineraryDetail.Sectors.forEach(sector => {
      totalFlights = totalFlights + sector.Flights.length;
    });
    if (this.itineraryDetail?.Sectors[currentSectorIndex]?.Flights[currentFlightIndex + 1] !== undefined) {
      this.selectFlight(this.itineraryDetail, currentSectorIndex,
        this.itineraryDetail?.Sectors[currentSectorIndex]?.Flights[currentFlightIndex + 1]);
      return true;
    } else if (this.itineraryDetail?.Sectors[currentSectorIndex + 1]?.Flights[0] !== undefined) {
      this.selectFlight(this.itineraryDetail, currentSectorIndex + 1,
        this.itineraryDetail?.Sectors[currentSectorIndex + 1]?.Flights[0]);
      this.facade.selectSectorItineraryDetail(this.itineraryDetail?.Sectors[currentSectorIndex + 1].SectorNumber);
      return true;
    }
    return false;
  }

  public selectFlight(itineraryDetail: ItineraryDetail, sectorNumber: number, flight: AirValidationFlightsSector): void {
    this.facade.selectFlightItineraryDetail(flight);
    this.facade.fetchSeatMapFlight(itineraryDetail.Id, itineraryDetail.AirReservationId,
      this.getFlightIx(itineraryDetail, sectorNumber, flight),
      itineraryDetail.Passengers as PassengersSeatMap[]);
  }

  public getFlightIx(itineraryDetail: ItineraryDetail, sectorNumber: number, flight: AirValidationFlightsSector): number {
    const allFlights: AirValidationFlightsSector[] = [];
    for (const _sector of itineraryDetail.Sectors) {
      for (const _flight of _sector.Flights) {
        allFlights.push(_flight);
      }
    }
    return allFlights.indexOf(flight);
  }

  private navigateToItinerary(): void {
    this.userId$.subscribe(userId => {
      this.appFacade.historyReset();
      this.router.navigate(['user-history', 'detail', userId, this.itineraryDetail.Id, ReservationType.Flight]);
    }).unsubscribe();
  }

  public registerAmplitudeEventErrors(): void {
    this.seatMapError$.pipe(take(1)).subscribe((seatMapError: SeatMapErrors) => {
      if (seatMapError?.ErrorType !== SeatMapErrorsTypes.SeatsUpdateSucces) {
        this.avoService.getEventTriggerProps$().pipe(
          tap(trigger => {
            return this.AvoDispatch.viewSeatmapError({
              ...this.avoService.avoBaseProperties(trigger),
              itineraryId: this.itineraryDetail?.Id,
              flightIndex: seatMapError?.FlightIdx?.toString()
            });
          }
          )).subscribe().unsubscribe();
      }
    }).unsubscribe();
  }

  public translate(resource: string): string {
    return this.translateService.translate(resource);
  }
} 
